import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sfxNav } from "@/utils/soundEngine";
import api from "@/api/client";
import clsx from "clsx";

/* ─── SVG Icon Set ──────────────────────────────────────────────────────────── */
function IconHome({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/>
      <path d="M9 21V12h6v9"/>
    </svg>
  );
}
function IconLearn({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10L12 5 2 10l10 5 10-5z"/>
      <path d="M6 12v5c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5"/>
    </svg>
  );
}
function IconQuiz({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18h6M10 22h4"/>
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
  );
}
function IconArena({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
}
function IconRank({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 21h8M12 17V3M17 8l-5-5-5 5"/>
    </svg>
  );
}
function IconShop({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  );
}
function IconMsg({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={active ? 2.3 : 1.7} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

const NAV_ITEMS = [
  { path: "/",          Icon: IconHome,  label: "Home"        },
  { path: "/lernen",    Icon: IconLearn, label: "Lernen"      },
  { path: "/arena",     Icon: IconArena, label: "Arena"       },
  { path: "/messages",  Icon: IconMsg,   label: "Nachrichten", badge: true },
  { path: "/shop",      Icon: IconShop,  label: "Shop"        },
];

export default function BottomNav() {
  const navigate     = useNavigate();
  const { pathname } = useLocation();

  // Unread message count for badge
  const { data: convData } = useQuery({
    queryKey:        ["conversations"],
    queryFn:         () => api.get("/messages/conversations", { timeout: 8000 }).then(r => r.data),
    refetchInterval: 20_000,
    staleTime:       5_000,
    retry:           1,
  });
  const unreadMsgs = (convData?.conversations || []).reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 safe-bottom">
      {/* Glass backdrop */}
      <div
        className="absolute inset-0 backdrop-blur-2xl"
        style={{
          background: "linear-gradient(180deg, transparent 0%, rgba(6,6,26,0.92) 20%, rgba(6,6,26,0.97) 100%)",
          borderTop: "1px solid rgba(139,92,246,0.14)",
        }}
      />

      {/* Top edge glow */}
      <div
        className="absolute top-0 left-0 right-0 h-px pointer-events-none"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.4) 20%, rgba(6,182,212,0.3) 50%, rgba(236,72,153,0.25) 80%, transparent 100%)" }}
      />

      <div className="relative max-w-2xl mx-auto flex h-16">
        {/* Sliding active pill — springt federnd zwischen den Tabs */}
        {(() => {
          const activeIdx = NAV_ITEMS.findIndex(
            ({ path }) => pathname === path || (path !== "/" && pathname.startsWith(path))
          );
          if (activeIdx < 0) return null;
          return (
            <div
              className="absolute top-1.5 h-10 pointer-events-none"
              style={{
                left: `${activeIdx * (100 / NAV_ITEMS.length)}%`,
                width: `${100 / NAV_ITEMS.length}%`,
                transition: "left 0.45s cubic-bezier(0.34,1.56,0.64,1)",
              }}
            >
              <div
                className="mx-auto w-12 h-10 rounded-2xl"
                style={{
                  background: "radial-gradient(ellipse at center, rgba(139,92,246,0.22) 0%, transparent 70%)",
                  boxShadow: "inset 0 0 0 1px rgba(139,92,246,0.14)",
                }}
              />
            </div>
          );
        })()}
        {NAV_ITEMS.map(({ path, Icon, label, badge }) => {
          const active    = pathname === path || (path !== "/" && pathname.startsWith(path));
          const badgeCount = badge ? unreadMsgs : 0;
          return (
            <button
              key={path}
              onClick={() => { sfxNav(); navigate(path); }}
              className={clsx(
                "relative flex-1 flex flex-col items-center justify-center gap-0.5",
                "transition-all duration-300 group",
                active ? "text-violet-400" : "text-gray-600 hover:text-gray-400"
              )}
            >

              {/* Icon — springs when its tab becomes active (keyed remount
                  retriggers the pop every switch) + optional badge */}
              <div
                key={active ? "on" : "off"}
                className={clsx(
                  "relative z-10 transition-all duration-300",
                  active ? "nav-pop scale-110 -translate-y-0.5" : "scale-100 group-hover:scale-105"
                )}
                style={active ? { filter: "drop-shadow(0 0 8px rgba(139,92,246,0.7))" } : undefined}
              >
                <Icon active={active} />
                {badgeCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-violet-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-0.5 leading-none"
                    style={{ boxShadow: "0 0 8px rgba(139,92,246,0.7)" }}>
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                )}
              </div>

              {/* Label */}
              <span
                className={clsx(
                  "text-[10px] font-bold tracking-wide z-10 transition-all duration-300",
                  active ? "text-violet-400 opacity-100" : "opacity-70"
                )}
              >
                {label}
              </span>

              {/* Bottom glow dot indicator */}
              <div
                className={clsx(
                  "absolute bottom-0.5 rounded-full transition-all duration-400",
                  active ? "w-5 h-0.5 opacity-100" : "w-0 h-0.5 opacity-0"
                )}
                style={active ? {
                  background: "linear-gradient(90deg, #8b5cf6, #06b6d4)",
                  boxShadow: "0 0 10px #8b5cf6",
                } : undefined}
              />
            </button>
          );
        })}
      </div>
    </nav>
  );
}
